import { CloseRounded, KeyboardArrowDownRounded } from '@mui/icons-material';
import {
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  ListItemButton,
  ListItemText,
  Popover,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { COLORS } from '../theme';

interface IOption {
  value: string;
  label: string;
}

/**
 * Selected rows float to the top (above a divider from the unselected rest),
 * matching legacy's "list of what's selected, with an X to clear" behavior
 * (flank, Daniel/Josh/Mike, product meeting 2026-10-08) — previously a
 * selection made halfway down a long list had no way back to the top short of
 * scrolling to find it again. Pure/exported so the ordering is testable
 * without a rendered DOM (no RTL in this repo, same constraint noted in
 * useFilterOptions.test.ts).
 */
export const splitBySelection = <T extends IOption>(
  rows: T[],
  selected: string[],
): { selectedRows: T[]; unselectedRows: T[] } => {
  const selectedSet = new Set(selected);
  const selectedRows: T[] = [];
  const unselectedRows: T[] = [];
  for (const row of rows) (selectedSet.has(row.value) ? selectedRows : unselectedRows).push(row);
  return { selectedRows, unselectedRows };
};

/**
 * The rows the dropdown actually renders: the current search match, plus any
 * selected value the search term would otherwise have excluded ("orphans").
 * Extracted from the component's render so flank#4738's "works while a
 * search term is typed" AC is directly testable — a selected row must never
 * disappear (and lose its pinned-to-top X) just because it doesn't match
 * whatever's currently typed in the search box.
 */
export const buildVisibleRows = <T extends IOption>(
  options: T[],
  search: string,
  selected: string[],
  labelOf?: (value: string) => string,
): T[] => {
  const term = search.trim().toLowerCase();
  const matches = term ? options.filter(option => option.label.toLowerCase().includes(term)) : options;

  // Always keep selected values in the list, even when the current search term
  // or scope no longer returns them. Otherwise a selection that scrolls out of
  // the result set is still applied to every query but has no row to untick,
  // leaving the user stuck with a filter they can't see or remove.
  const shown = new Set(matches.map(option => option.value));
  const orphans = selected
    .filter(value => !shown.has(value))
    .map(value => options.find(option => option.value === value) ?? ({ value, label: labelOf?.(value) ?? value } as T));

  return orphans.length ? [...orphans, ...matches] : matches;
};

/**
 * flank#4738 AC: "When more than one item is selected, a 'Clear' option
 * removes them all at once." A single selection already has its own X; the
 * bulk control only earns its place once there's more than one to clear.
 */
export const shouldShowClearAll = (selected: string[]): boolean => selected.length > 1;

interface IProps {
  label: string;
  options: IOption[];
  selected: string[];
  loading?: boolean;
  onChange: (selected: string[]) => void;
  /** Render the control non-interactive (e.g. a filter that isn't wired yet). */
  disabled?: boolean;
  /** Tooltip explaining why the control is disabled. */
  disabledReason?: string;
  /** Supply to push the typeahead term to the server (debounced 300ms). Needed
   *  because the option list is capped server-side, so filtering only the
   *  fetched page can never reach values past the cap. */
  onSearchChange?: (term: string) => void;
  /**
   * Label for a selected value the current `options` don't include (a search
   * term or scope change moved it out). Needed where values are ids rather than
   * names, or the orphan row shows the raw id (#4614).
   */
  labelOf?: (value: string) => string;
  /**
   * Keyword/wildcard filtering (Custom Dashboards, FB-007) — opt-in: only
   * rendered when both keyword props are supplied, so Unified Reporting's
   * usage of this component is completely unaffected. `keywordTerms` are the
   * currently-applied contains-terms for this dimension (ADDITIVE to
   * `selected`, not a replacement); `onKeywordTermsChange` persists edits.
   */
  keywordTerms?: string[];
  onKeywordTermsChange?: (terms: string[]) => void;
  /** Same shape as `keywordTerms`/`onKeywordTermsChange`, but for exclude (NOT-contains) terms. */
  excludeTerms?: string[];
  onExcludeTermsChange?: (terms: string[]) => void;
}

/**
 * Multiselect filter dropdown matching the FLANK 2.0 filter bar: a styled
 * button (label + active count + chevron) that opens a searchable, scrollable
 * checkbox list. The label renders as its own text node with the exact label
 * string so the locked e2e (getByText exact) matches.
 */
const MultiSelectFilter = ({
  label,
  options,
  selected,
  loading,
  onChange,
  disabled = false,
  disabledReason,
  onSearchChange,
  labelOf,
  keywordTerms,
  onKeywordTermsChange,
  excludeTerms,
  onExcludeTermsChange,
}: IProps) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [search, setSearch] = useState('');
  const keywordsEnabled = onKeywordTermsChange != null;
  const excludeEnabled = onExcludeTermsChange != null;
  const terms = keywordTerms ?? [];
  const excludes = excludeTerms ?? [];

  const addKeyword = () => {
    const term = search.trim();
    if (!term || !onKeywordTermsChange || terms.some(t => t.toLowerCase() === term.toLowerCase())) return;
    onKeywordTermsChange([...terms, term]);
    setSearch('');
  };

  const removeKeyword = (term: string) => {
    onKeywordTermsChange?.(terms.filter(t => t !== term));
  };

  const addExclude = () => {
    const term = search.trim();
    if (!term || !onExcludeTermsChange || excludes.some(t => t.toLowerCase() === term.toLowerCase())) return;
    onExcludeTermsChange([...excludes, term]);
    setSearch('');
  };

  const removeExclude = (term: string) => {
    onExcludeTermsChange?.(excludes.filter(t => t !== term));
  };

  // When the caller wants server-side search, debounce the term up to it — the
  // option list is capped server-side, so filtering the fetched page alone can't
  // reach values past the cap. Local filtering below still runs and is a no-op
  // once the server has already narrowed to the same term.
  useEffect(() => {
    if (!onSearchChange) return;
    const id = setTimeout(() => onSearchChange(search.trim()), 300);
    return () => clearTimeout(id);
  }, [search, onSearchChange]);

  const active = selected.length > 0 || terms.length > 0 || excludes.length > 0;
  const activeCount = selected.length + terms.length + excludes.length;
  const open = Boolean(anchorEl) && !disabled;

  const filtered = useMemo(
    () => buildVisibleRows(options, search, selected, labelOf),
    [options, search, selected, labelOf],
  );

  // Grouped for rendering so selected rows float to the top regardless of
  // where they sort in `filtered` (see splitBySelection above).
  const { selectedRows, unselectedRows } = useMemo(() => splitBySelection(filtered, selected), [filtered, selected]);

  const toggle = (value: string) => {
    onChange(selected.includes(value) ? selected.filter(v => v !== value) : [...selected, value]);
  };

  const button = (
    <Button
      variant='outlined'
      disableElevation
      disabled={disabled}
      onClick={event => !disabled && setAnchorEl(event.currentTarget)}
      endIcon={<KeyboardArrowDownRounded sx={{ color: COLORS.muted }} />}
      sx={{
        justifyContent: 'space-between',
        textTransform: 'none',
        // 150/1.75 used to push Unified Reporting's 5-6 filters + date pill past
        // what a laptop-width viewport fits on one row, stranding the date pill
        // alone on row 2 (#4572). A floor, not a cap — content-driven width still
        // wins for a longer label, so this never truncates anything.
        minWidth: 130,
        px: 1.5,
        py: 1.1,
        borderRadius: '10px',
        bgcolor: '#fff',
        color: COLORS.text,
        borderColor: active ? COLORS.primaryBorder : COLORS.border,
        '&:hover': { bgcolor: '#fff', borderColor: active ? COLORS.primaryBorder : COLORS.muted },
      }}
    >
      <Box component='span' sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
        <Typography component='span' variant='body2' sx={{ fontWeight: 500 }}>
          {label}
        </Typography>
        {active && (
          <Typography component='span' variant='body2' sx={{ color: COLORS.primary, fontWeight: 600 }}>
            · {activeCount}
          </Typography>
        )}
      </Box>
    </Button>
  );

  return (
    <>
      {disabled && disabledReason ? (
        <Tooltip title={disabledReason} placement='top' arrow>
          {/* span keeps the tooltip working over a disabled button */}
          <span style={{ display: 'inline-flex' }}>{button}</span>
        </Tooltip>
      ) : (
        button
      )}

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        slotProps={{
          paper: {
            sx: {
              mt: 0.5,
              width: 260,
              borderRadius: '10px',
              border: `1px solid ${COLORS.border}`,
              boxShadow: '0 8px 24px rgba(0,0,0,0.08)',
            },
          },
        }}
      >
        <Box sx={{ p: 1 }}>
          <TextField
            size='small'
            fullWidth
            autoFocus
            placeholder={
              keywordsEnabled || excludeEnabled ? 'Search, or press Enter to add as a keyword filter' : 'Search'
            }
            value={search}
            onChange={event => setSearch(event.target.value)}
            onKeyDown={
              keywordsEnabled || excludeEnabled
                ? event => {
                    if (event.key === 'Enter') (keywordsEnabled ? addKeyword : addExclude)();
                  }
                : undefined
            }
          />
          {shouldShowClearAll(selected) && (
            <Button
              size='small'
              onClick={() => onChange([])}
              sx={{ mt: 0.5, textTransform: 'none', fontSize: 12, p: 0, minWidth: 0, display: 'block' }}
            >
              Clear {selected.length} selected
            </Button>
          )}
          {keywordsEnabled && search.trim() && (
            <Button
              size='small'
              onClick={addKeyword}
              sx={{ mt: 0.5, textTransform: 'none', fontSize: 12, p: 0, minWidth: 0, display: 'block' }}
            >
              Include everything matching "{search.trim()}"
            </Button>
          )}
          {excludeEnabled && search.trim() && (
            <Button
              size='small'
              onClick={addExclude}
              sx={{ mt: 0.5, textTransform: 'none', fontSize: 12, p: 0, minWidth: 0, display: 'block' }}
            >
              Exclude everything matching "{search.trim()}"
            </Button>
          )}
          {(keywordsEnabled && terms.length > 0) || (excludeEnabled && excludes.length > 0) ? (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
              {terms.map(term => (
                <Chip
                  key={`contains-${term}`}
                  size='small'
                  label={`contains "${term}"`}
                  onDelete={() => removeKeyword(term)}
                  deleteIcon={<CloseRounded sx={{ fontSize: 14 }} />}
                />
              ))}
              {excludes.map(term => (
                <Chip
                  key={`excludes-${term}`}
                  size='small'
                  label={`excludes "${term}"`}
                  onDelete={() => removeExclude(term)}
                  deleteIcon={<CloseRounded sx={{ fontSize: 14 }} />}
                />
              ))}
            </Box>
          ) : null}
        </Box>
        <Box sx={{ maxHeight: 280, overflowY: 'auto', pb: 0.5 }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
              <CircularProgress size={20} sx={{ color: COLORS.primary }} />
            </Box>
          ) : filtered.length === 0 ? (
            <Typography variant='body2' sx={{ color: COLORS.muted, textAlign: 'center', py: 2 }}>
              No options
            </Typography>
          ) : (
            <>
              {selectedRows.map(option => (
                <ListItemButton key={option.value} dense onClick={() => toggle(option.value)} sx={{ py: 0.25 }}>
                  <Checkbox
                    edge='start'
                    size='small'
                    disableRipple
                    checked
                    sx={{ color: COLORS.muted, '&.Mui-checked': { color: COLORS.primary } }}
                  />
                  <ListItemText primary={option.label} primaryTypographyProps={{ variant: 'body2' }} />
                  <IconButton
                    size='small'
                    aria-label={`Remove ${option.label}`}
                    onClick={event => {
                      // Stop this from also bubbling to the row's own onClick,
                      // which would toggle the same value a second time.
                      event.stopPropagation();
                      toggle(option.value);
                    }}
                  >
                    <CloseRounded sx={{ fontSize: 16, color: COLORS.muted }} />
                  </IconButton>
                </ListItemButton>
              ))}
              {selectedRows.length > 0 && unselectedRows.length > 0 && <Divider sx={{ my: 0.5 }} />}
              {unselectedRows.map(option => (
                <ListItemButton key={option.value} dense onClick={() => toggle(option.value)} sx={{ py: 0.25 }}>
                  <Checkbox
                    edge='start'
                    size='small'
                    disableRipple
                    checked={false}
                    sx={{ color: COLORS.muted, '&.Mui-checked': { color: COLORS.primary } }}
                  />
                  <ListItemText primary={option.label} primaryTypographyProps={{ variant: 'body2' }} />
                </ListItemButton>
              ))}
            </>
          )}
        </Box>
      </Popover>
    </>
  );
};

export default MultiSelectFilter;
